<template>
  <section
    data-testid="notification-component"
    :class="[
      'text-royal-blue-160',
      'gap-3',
      'rounded-sm',
      'p-4',
      'md:p-8',
      'border-2',
      ...computedClasses,
    ]"
    :role="computedRole"
    :aria-live="computedAriaLive"
  >
    <component
      :is="`h${headingLevel}`"
      class="flex gap-x-2 font-bold mb-2"
      data-testid="notification-heading"
    >
      <span
        v-if="includeIcon"
        class="shrink-0">
        <slot name="icon">
          <SuccessIcon
            v-if="variant === 'success'"
            data-testid="icon-success"
            title="Success:"
          />
          <WarningTriangleIcon
            v-else-if="variant === 'error'"
            data-testid="icon-error"
            title="Error:"
          />
          <InformationIcon
            v-else-if="variant === 'information'"
            data-testid="icon-information"
            title="For information:"
          />
          <ExclamationCircleIcon
            v-else-if="variant === 'warning'"
            data-testid="icon-warning"
            title="Warning:"
          />
        </slot>
      </span>
      {{ headingText }}
    </component
    >
    <div>
      <slot name="body">
      </slot>
    </div>
  </section>
</template>

<script>
import InformationIcon from './icons/InformationIcon.vue'
import SuccessIcon from './icons/SuccessIcon.vue'
import ExclamationCircleIcon from './icons/ExclamationCircleIcon.vue'
import WarningTriangleIcon from './icons/WarningTriangleIcon.vue'

// variant prop mapped to respective class names.
export const classMap = {
  warning: ['bg-yellow-5', 'border-yellow'],
  success: ['bg-lime-5', 'border-lime'],
  error: ['bg-ruby-red-5', 'border-ruby-red'],
  information: ['bg-science-blue-5', 'border-science-blue-100'],
}

/** Notification components are to place emphasis on messages that are important for users to see. They are used to highlight important information, such as warnings or errors, or to provide additional information to help users complete a task. */
export default {
  components: {
    InformationIcon,
    SuccessIcon,
    ExclamationCircleIcon,
    WarningTriangleIcon,
  },
  props: {
    /** Heading level (1 to 6) of the notification. */
    headingLevel: {
      type: Number,
      default: 2,
      validator: value => [1, 2, 3, 4, 5, 6].includes(value),
    },
    /** Wording for the heading of the notification. */
    headingText: {
      type: String,
      required: true,
    },
    /** Colour variations for type of message. */
    variant: {
      type: String,
      default: 'information',
      validator: value =>
        ['information', 'success', 'error', 'warning'].includes(value),
    },
    /** Choose whether an icon should be displayed/included in the markup. */
    includeIcon: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    computedClasses() {
      return classMap[this.variant]
    },
    computedRole() {
      return ['error', 'warning'].includes(this.variant) ? 'alert' : 'status'
    },
    computedAriaLive() {
      return ['error', 'warning'].includes(this.variant) ? 'assertive' : 'polite'
    },
  },
}
</script>
